/**
 * ⭐ **THE ONE BUTTON DEFINITION**, and the deck says that is the right shape rather than my
 * preference: measured across `mockups/*.html`, **all 163 buttons carry `class="btn"`**. Zero
 * hand-rolled ones. Our product had gone the other way — **91 buttons across 27 files**, every one
 * styled at its own call site.
 *
 * ## The owner's report, and what the measurements said about it
 *
 * > *"Button styling in light mode is also wrong across the board. Nothing looks like a button. I'm
 * > fine with subtle but it's too subtle."*
 *
 * Three numbers explain that sentence completely:
 *
 * 1. The deck's `.btn` is **outline-only** — no fill at rest — so the border is the entire
 *    affordance. `border-subtle` against the page measures **1.34:1 in light** (WCAG 1.4.11 wants
 *    3:1) versus **2.70:1 in dark**. At 1.34:1 an outline is not subtle, it is absent — and that is
 *    exactly why he named light and not dark. Fixed by `--color-border-control`, calibrated per
 *    polarity (see `index.css`).
 * 2. Hover was no better: `surface-muted` on `surface` is **1.03:1 in light**. So the button had an
 *    invisible edge *and* an invisible hover. ⇒ Hover here also moves the border to
 *    `border-strong`, because the fill alone cannot carry a state change in light mode.
 * 3. ⛔ **The deck gives most screens a dominant action; our product had TWO filled buttons in the
 *    whole console.** So most views had no dominant action at all — Von Restorff in reverse. ⚠️ Two
 *    independent counts of the deck's primaries disagree on the total because they count differently,
 *    so **no ratio is quoted here on purpose** — the authority is the per-screen list in the
 *    primary-action proposal, not a percentage. Either way it is a per-screen judgment and NOT this
 *    file's to make: `tone` is a prop, and who passes `'primary'` is the caller's decision (and, for
 *    lifecycle actions, `dev-actions`' `severity` mapping). This file only guarantees each tone is
 *    *visible*, and that the button and link forms are indistinguishable.
 *
 * ⚠️ **Calibration, not escalation.** The rest state is still an outline with no fill — the owner
 * said subtle is fine. Nothing here makes a button heavier than the deck's; it makes it visible.
 *
 * ## Sizes follow the deck's own distribution
 *
 * `btn--sm` is **73%** of the deck's usage, and its metrics (`4px 10px`, `text-xs`) are what our
 * existing buttons already are — so `'sm'` is the default and adopting this component does not
 * resize the product. `'md'` is the deck's base `.btn` (`7px 14px`, `text-sm`); `'micro'` is
 * `.btn--micro` (`2px 8px`, 10px), used 34 times in the deck for in-row actions.
 *
 * ## ⛔ Things not to "tidy up"
 *
 * · **`focus-visible` is not part of the subtle calibration.** A control that is more visible at
 *   rest and less visible when focused is a net regression, so the ring is unconditional and
 *   identical across tones.
 * · **Disabled is `opacity-40` + `cursor-not-allowed` and it neutralises hover** (the deck's
 *   `.btn[disabled]:hover { background: none }`). Without that, a disabled button still lights up
 *   under the pointer and reads as clickable.
 * · **`text-on-accent` on BOTH filled tones**, including danger. Measured: white on the light
 *   critical `#dc2626` is 4.80:1 and black on the dark critical `#ff5578` is 7.24:1 — whereas a
 *   hardcoded `#fff` (which is what the deck's `.btn--danger` uses) would be **2.9:1** on the dark
 *   critical. The deck is wrong here and this is a deliberate divergence.
 */
import type { ButtonHTMLAttributes, Ref, ReactNode } from 'react';
import { ARIA_DISABLED_CLASS } from '../ward/class-tokens';

/**
 * How loud the control is. ⛔ NOT "what the action does" — this file has no opinion on which action
 * deserves which tone, and must not grow one. Consequence-to-tone mapping belongs to the caller
 * (for data-plane lifecycle actions, to `dp-lifecycle`'s `severity`).
 */
export type ButtonTone = 'default' | 'primary' | 'danger' | 'ghost' | 'chrome' | 'disclosure';

export type ButtonSize = 'micro' | 'sm' | 'md';

/**
 * Shared by every tone and size: shape, alignment, motion, focus, disabled.
 *
 * ## ⭐ `justify-center` — added for the pre-auth cards, and PROVABLY INERT everywhere else
 *
 * `items-center` centres on the CROSS axis; nothing centred on the main one. For every button in the
 * console that was invisible, because an `inline-flex` box is content-sized and `justify-content` has
 * no free space to distribute. ⛔ **It stops being invisible the moment a button is STRETCHED** — the
 * four pre-auth surfaces are `flex flex-col` cards, where `align-items: stretch` makes a 320px-wide
 * button whose label then hugs the left edge. Their hand-rolled `<button>`s never showed this because
 * the UA's own `text-align: center` was doing the work; adopting this component threw that away.
 *
 * ⚠️ Why it is safe as a base-class change rather than a `fullWidth` prop: **no call site can give a
 * `Button` a width** — `className` is deliberately not forwardable (see {@link ButtonProps}) — so the
 * only elements this token can reach are ones a parent stretched, and a stretched button with an
 * off-centre label is a defect at every one of them. ⛔ A `fullWidth` prop would instead be a second
 * way to say what the parent's layout already says, i.e. the configuration-creep this file exists to
 * refuse. `button-link-parity.test.ts` stays green because both components derive from
 * {@link buttonClass} rather than re-assembling the string.
 *
 * ⚠️ The deck does not answer this: its `.btn` is `inline-flex; align-items:center` with no
 * `justify-content`, and it never stretches a button — every one of its 163 is an in-row action.
 */
export const BUTTON_BASE_CLASS =
  'inline-flex items-center justify-center gap-1.5 rounded-md border font-medium transition-colors ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-40 ' +
  // A held button keeps its Tab stop and takes the one held look of the WARD parts.
  ARIA_DISABLED_CLASS;

/**
 * Each entry also repeats that clause under `aria-disabled:`, so a held button shows no hover either.
 *
 * ⛔ Every entry ends with a `disabled:` clause that RESTORES the rest state, which is what makes
 * `.btn[disabled]:hover { background: none }` work through Tailwind's hover-wins-by-order rule.
 * Dropping it leaves a disabled control that highlights on hover — the clearest possible false
 * affordance.
 */
export const BUTTON_TONE_CLASS: Record<ButtonTone, string> = {
  default:
    'border-border-control text-secondary hover:border-border-strong hover:bg-surface-muted hover:text-primary ' +
    'disabled:border-border-control disabled:bg-transparent disabled:text-secondary ' +
    'aria-disabled:border-border-control aria-disabled:bg-transparent aria-disabled:text-secondary',
  primary:
    'border-accent bg-accent text-on-accent hover:bg-accent/90 ' +
    'disabled:border-accent disabled:bg-accent disabled:text-on-accent ' +
    'aria-disabled:border-accent aria-disabled:bg-accent aria-disabled:text-on-accent',
  danger:
    'border-state-critical bg-state-critical text-on-accent hover:bg-state-critical/90 ' +
    'disabled:border-state-critical disabled:bg-state-critical disabled:text-on-accent ' +
    'aria-disabled:border-state-critical aria-disabled:bg-state-critical aria-disabled:text-on-accent',
  // The deck's `.btn--ghost` is `.btn` with a transparent border — a real button that is quiet
  // until hovered. ⚠️ Use it only where a visible neighbour already carries the affordance.
  ghost:
    'border-transparent text-secondary hover:bg-surface-muted hover:text-primary ' +
    'disabled:bg-transparent disabled:text-secondary ' +
    'aria-disabled:bg-transparent aria-disabled:text-secondary',
  /**
   * ⭐⭐ **A BUTTON THAT SITS ON `--color-chrome`** — the nav rail and the top bar. Added for the app
   * picker in `NavRail`, on the variant path that the `className` prop's docblock prescribes, because it
   * cannot be a `className` on `default`.
   *
   * ## ⛔ WHY `default` IS NOT CLOSE ENOUGH, AND THE DIFFERENCE IS ONE TOKEN — MEASURED
   *
   * `default` and this are byte-identical except for the hover fill, and on chrome `default`'s fill
   * **does not exist**: `hover:bg-surface-muted` over `--color-chrome` is
   *
   *     light   surface-muted rgb(245 247 250) on chrome rgb(245 247 250)   = 1.0000:1
   *
   * ⇒ *the same colour.* A `default`-tone button in the rail has **no hover state at all in light
   * mode** — R9 **N-6 defect D-A**, the exact defect `--color-chrome-hover` was minted to fix, and one
   * this rail has already paid for once. `--color-chrome-hover` is a `color-mix(… , transparent)`, so
   * it **cannot alias a surface token**: it composites over whatever is beneath it, measuring
   * **1.1164 light / 1.1291 dark** on chrome.
   *
   * ⛔ Do NOT "unify" this with `default`, and do not reach for `default` in rail/top-bar chrome. The
   * two differ by one token precisely because they sit on two different grounds.
   */
  chrome:
    'border-border-control text-secondary hover:border-border-strong hover:bg-chrome-hover hover:text-primary ' +
    'disabled:border-border-control disabled:bg-transparent disabled:text-secondary ' +
    'aria-disabled:border-border-control aria-disabled:bg-transparent aria-disabled:text-secondary',
  /**
   * The rail's Settings title, a section title that opens its section. No border, no fill at rest and
   * no shadow, so with the `micro` size its text lines up with the static titles. `border-0` beats the
   * base `border` by stylesheet order, and a test reads the compiled CSS to pin that.
   */
  disclosure:
    'w-full border-0 bg-transparent text-primary hover:bg-chrome-hover ' +
    'disabled:bg-transparent disabled:text-primary ' +
    'aria-disabled:bg-transparent aria-disabled:text-primary',
};

/** The deck's three sizes, in its own values. */
export const BUTTON_SIZE_CLASS: Record<ButtonSize, string> = {
  micro: 'px-2 py-0.5 text-2xs',
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3.5 py-[7px] text-sm',
};

/**
 * ⭐ **THE ONE DEFINITION OF THE COMPOSED TREATMENT**, and the reason it is a function rather than
 * three exported strings each caller concatenates.
 *
 * There are now TWO components — `Button` (a `<button>`) and `ButtonLink` (an `<a>`), because 7 of
 * the 11 page-level primaries are navigations and forcing those into a `<button>` would throw away
 * right-click, middle-click and copy-link, i.e. the entire point of having a URL. Two components mean
 * two places the string could be assembled, and *"one definition of the parts"* is not the same
 * property as *"one definition of the result"*: `${BASE} ${TONE} ${SIZE}` written twice can drift in
 * order, spacing, or by one of them quietly gaining a fourth term.
 *
 * So both components call this, and `button-link-parity.test.ts` asserts the rendered class string of
 * each is byte-identical to this function's output for **every** tone x size pair. ⛔ If the two ever
 * diverge we are back to 91 hand-styled call sites with extra steps.
 */
export function buttonClass({ tone, size }: { readonly tone: ButtonTone; readonly size: ButtonSize }): string {
  return `${BUTTON_BASE_CLASS} ${BUTTON_TONE_CLASS[tone]} ${BUTTON_SIZE_CLASS[size]}`;
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  /**
   * ⚠️ Declared explicitly because `ButtonHTMLAttributes` does NOT include `ref` — it lives on
   * `RefAttributes`, which `Omit<…>` of the former does not pick up. React 19 treats `ref` as an
   * ordinary prop, so it forwards through `...rest` with no `forwardRef` wrapper.
   *
   * It exists for **focus restoration**: a menu or dialog trigger must be able to return focus to
   * itself on close, and a trigger that cannot is a keyboard trap — the user is dumped at the top of
   * the document with no way back to where they were.
   */
  readonly ref?: Ref<HTMLButtonElement>;
  readonly tone?: ButtonTone;
  readonly size?: ButtonSize;
  readonly children: ReactNode;
  /**
   * ⛔ `className` is DELIBERATELY NOT FORWARDABLE (it is `Omit`ted above), and that is the whole
   * point of the component rather than an exported class string. A forwardable `className` is how
   * "one definition" decays back into 86: the first call site that needs a tweak adds
   * `className="border-border-subtle"` and the visibility fix is silently reverted for that button,
   * with nothing to notice it. Layout around a button belongs to the parent; a new *variant* belongs
   * in `BUTTON_TONE_CLASS` where every screen gets it.
   */
  readonly type?: 'button' | 'submit' | 'reset';
}

export function Button({
  tone = 'default',
  size = 'sm',
  type = 'button',
  children,
  ...rest
}: ButtonProps): React.JSX.Element {
  return (
    <button
      {...rest}
      type={type}
      className={buttonClass({ tone, size })}
    >
      {children}
    </button>
  );
}
